import React, { useState } from 'react';
import { LuChevronDown, LuCircleMinus } from 'react-icons/lu';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { WEIGHT_INPUT_REGEX } from '@/components/ui/WeightInput';
import { cn } from '@/utils/cn';

interface ModelStrategyRowProps {
    badgeLabel: string;
    badgeTone?: 'claro' | 'blackrock' | 'neutral';
    name: string;
    description: string;
    details: React.ReactNode;
    defaultExpanded?: boolean;
    weight: string;
    weightError?: string;
    onUpdateWeight: (weight: string) => void;
    onRemove: () => void;
}

export const ModelStrategyRow: React.FC<ModelStrategyRowProps> = ({
    badgeLabel,
    badgeTone = 'neutral',
    name,
    description,
    details,
    defaultExpanded = false,
    weight,
    weightError,
    onUpdateWeight,
    onRemove,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleWeightChange = (rawValue: string) => {
        if (rawValue === '' || WEIGHT_INPUT_REGEX.test(rawValue)) {
            const normalized = rawValue.replace(',', '.');
            const numValue = parseFloat(normalized);
            if (normalized === '' || (numValue >= 0 && numValue <= 100)) {
                onUpdateWeight(normalized);
            }
        }
    };

    return (
        <Collapsible open={expanded} onOpenChange={setExpanded} className="border-cocoa-200 rounded-lg border bg-white">
            <div className="flex items-center gap-4 px-5 py-4">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="hover:bg-cocoa-0 -mx-2 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-left"
                    >
                        <LuChevronDown
                            aria-hidden
                            className={cn(
                                'text-cocoa-500 h-4 w-4 shrink-0 transition-transform',
                                expanded && 'rotate-180',
                            )}
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <span
                                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
                                        badgeTone === 'claro'
                                            ? 'bg-seafoam-0 text-seafoam-600'
                                            : badgeTone === 'blackrock'
                                              ? 'bg-black text-white'
                                              : 'bg-cocoa-0 text-cocoa-500'
                                    }`}
                                >
                                    {badgeLabel}
                                </span>
                                <span className="text-cocoa-1000 truncate text-sm font-semibold">{name}</span>
                            </div>
                            <span className="text-cocoa-500 truncate text-xs">{description}</span>
                        </div>
                    </button>
                </CollapsibleTrigger>
                <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-1">
                        <div
                            className={`flex h-9 w-[78px] items-center justify-end gap-0.5 rounded-md border px-2 ${
                                weightError ? 'border-red-500' : 'border-cocoa-200'
                            }`}
                        >
                            <input
                                type="text"
                                inputMode="decimal"
                                aria-label={`Weight of ${name}`}
                                value={weight}
                                onChange={(e) => handleWeightChange(e.target.value)}
                                className="text-cocoa-1000 w-8 border-0 bg-transparent text-right text-sm font-medium outline-none"
                            />
                            <span className="text-cocoa-500 text-sm">%</span>
                        </div>
                        {weightError && <span className="text-xs text-red-600">{weightError}</span>}
                    </div>
                    <button
                        type="button"
                        aria-label={`Remove ${name}`}
                        onClick={onRemove}
                        className="text-cocoa-400 hover:bg-cocoa-0 flex h-9 w-9 items-center justify-center rounded-md hover:text-red-600"
                    >
                        <LuCircleMinus className="h-4.5 w-4.5" />
                    </button>
                </div>
            </div>
            <CollapsibleContent>
                <div className="border-cocoa-200/50 border-t px-5 py-4">{details}</div>
            </CollapsibleContent>
        </Collapsible>
    );
};
